import {Action, Attributes, AttributesRow, Card, DataStatus, Flag} from 'platform/components';
import {Box, Link, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {equals, isNil, isNotNil, not} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchListQuery, useGetCurrenciesQuery} from '@dms/api/core';
import {useGetPaymentTypeEnumQuery} from '@dms/api/finance';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {TenantResponseBody} from '@dms/api/shared';
import {
  GetWarehouseResponse,
  ReceiveNoteDeliveryNoteSlice,
  ReceiveNoteGeneralInfoSlice,
  ReceiveNoteStateSlice,
  useGetDeliveryNoteQuery,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {getCustomerName, useGetSupplierCustomersQuery, usePermissions} from '@dms/shared';
import {getSupplierById, getWarehouseById} from '@dms/warehouse/shared';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {BasicInformationForm} from './BasicInformationForm';
import {BasicInformationReadOnly} from './BasicInformationReadOnly';
import {BranchBasedInformationForm} from './BranchBasedInformationForm';
import {BranchBasedInformationReadOnly} from './BranchBasedInformationReadOnly';

interface GeneralInformationTabProps extends RequiredTestIdProps {
  receiveNoteId: string;
  receiveNoteState: ReceiveNoteStateSlice | Nullish;
  receiveNoteGeneralInfo: ReceiveNoteGeneralInfoSlice | Nullish;
  receiveNoteDeliveryNote: ReceiveNoteDeliveryNoteSlice | Nullish;
  tenant: TenantResponseBody | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  isDraftReceiveNote: boolean;
  isFromReceiveNote: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export const GeneralInformationTab = (props: GeneralInformationTabProps) => {
  const dateTimeFormatter = useDateTimeFormatter();

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [isReadOnly, setIsReadOnly] = useState(isNotNilOrEmpty(props.receiveNoteId));

  const isBranchBased =
    props.receiveNoteState?.type === 'TRANSFER' ||
    props.receiveNoteState?.type === 'EXTERNAL_TRANSFER' ||
    props.receiveNoteState?.type === 'OPENING_BALANCE';

  const {data: authorizationProfiles, isLoading: isAuthorizationProfilesLoading} =
    useGetAuthorizationProfilesQuery(
      {'x-tenant': props.tenant?.id ?? ''},
      {skip: isNil(props.tenant)}
    );

  const {
    data: suppliers,
    isLoading: isSuppliersLoading,
    isError: isSuppliersError,
  } = useGetSupplierCustomersQuery();

  const {
    data: branchList,
    isLoading: isBranchListLoading,
    isError: hasBranchListError,
  } = useGetBranchListQuery();

  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: paymentTypes,
    isLoading: isPaymentTypesLoading,
    isError: isPaymentTypesError,
  } = useGetPaymentTypeEnumQuery();

  const {
    data: currencies,
    isLoading: isCurrenciesLoading,
    isError: isCurrenciesError,
  } = useGetCurrenciesQuery();

  const {
    data: deliveryNote,
    isLoading: isDeliveryNoteLoading,
    isError: isDeliveryNoteError,
  } = useGetDeliveryNoteQuery(
    {deliveryNoteId: props.receiveNoteDeliveryNote?.deliveryNote?.deliveryNoteId as string},
    {skip: isNilOrEmpty(props.receiveNoteDeliveryNote?.deliveryNote?.deliveryNoteId)}
  );

  const isLoading =
    isAuthorizationProfilesLoading ||
    isSuppliersLoading ||
    isBranchListLoading ||
    isWarehousesLoading ||
    isPaymentTypesLoading ||
    isCurrenciesLoading ||
    isDeliveryNoteLoading;

  const isError =
    isSuppliersError ||
    hasBranchListError ||
    isWarehousesError ||
    isPaymentTypesError ||
    isCurrenciesError ||
    isDeliveryNoteError;

  const isReceiveNoteCompleted = props.receiveNoteState?.state === 'COMPLETED';

  const paymentTypeByReceiveNote = paymentTypes?.find((paymentType) =>
    equals(paymentType.key, props.receiveNoteGeneralInfo?.paymentType)
  );

  const currencyByWarehouse = currencies?.find((currency) =>
    equals(currency.code, props.warehouse?.currency)
  );

  const authorizationProfileByReceiveNote = authorizationProfiles?.find((authorizationProfile) =>
    equals(authorizationProfile.id, props.receiveNoteGeneralInfo?.authorizationProfileId)
  );

  const supplierByReceiveNote = getSupplierById(
    suppliers ?? [],
    props.receiveNoteGeneralInfo?.supplierId
  );

  const warehouseByReceiveNote = getWarehouseById(
    warehouses,
    props.receiveNoteGeneralInfo?.warehouseId
  );

  const sourceBranchByDeliveryNote = branchList?.branchListItems?.find((branch) =>
    equals(branch.id, props.receiveNoteGeneralInfo?.transferSourceBranchId)
  );

  const branchByDeliveryNote = branchList?.branchListItems?.find((branch) =>
    equals(branch.id, props.receiveNoteGeneralInfo?.branchId)
  );

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const deliveryNoteAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: isNotNil(deliveryNote?.deliveryNoteId) ? (
        <Link
          title={deliveryNote?.deliveryNoteNumber ?? EMPTY_PLACEHOLDER}
          rightIcon="action/open_in_new"
          size="small"
          onClick={() =>
            window.open(
              composePath(warehouseRoutes.deliveryNoteDetailOverview, {
                params: {id: deliveryNote?.deliveryNoteId},
              }),
              '_blank'
            )
          }
          data-testid={suffixTestId('deliveryNoteLink', props)}
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.warehouse'),
      value: props.warehouse?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplier'),
      value: getCustomerName(supplierByReceiveNote),
    },
    {
      label: i18n.t('entity.warehouse.labels.deliveryType'),
      value: match(props.receiveNoteState?.type)
        .with('OPENING_BALANCE', () => i18n.t('entity.warehouse.labels.openingBalance'))
        .with('PURCHASE', () => i18n.t('entity.warehouse.labels.purchase'))
        .with('TRANSFER', () => i18n.t('entity.warehouse.labels.transfer'))
        .with('EXTERNAL_TRANSFER', () => i18n.t('entity.warehouse.labels.externalTransfer'))
        .with('CORE_EXCHANGE', () => i18n.t('entity.warehouse.labels.coreExchange'))
        .otherwise(() => EMPTY_PLACEHOLDER),
    },
    {
      label: i18n.t('entity.warehouse.labels.issuedDate'),
      value: formatToShortDate(deliveryNote?.issuedAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.createdDate'),
      value: formatToShortDate(props.receiveNoteDeliveryNote?.deliveryNote?.createdAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.completedAt'),
      value: formatToShortDate(props.receiveNoteDeliveryNote?.deliveryNote?.completedAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.createdFromReceiveNote'),
      content: (
        <Flag
          isSubtle
          colorScheme="blue"
          label={
            props.isFromReceiveNote ? i18n.t('general.labels.yes') : i18n.t('general.labels.no')
          }
          size="small"
          data-testid={suffixTestId('isFromReceiveNoteFlag', props)}
        />
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.note'),
      value: props.receiveNoteDeliveryNote?.deliveryNote?.note,
    },
  ];

  const basicInformationCardActions = buildArray<Action>().when(
    isNotNilOrEmpty(props.receiveNoteId) && isReadOnly,
    {
      type: 'button',
      variant: 'link',
      leftIcon: 'image/edit',
      title: i18n.t('general.labels.edit'),
      isDisabled: not(canEditReceiveNote),
      onClick: () => setIsReadOnly(false),
    }
  );

  const renderBasicInformation = (isReadOnly: boolean, isBranchBased: boolean) =>
    match({isReadOnly, isBranchBased})
      .with({isReadOnly: true, isBranchBased: true}, () => (
        <BranchBasedInformationReadOnly
          receiveNoteState={props.receiveNoteState}
          receiveNoteGeneralInfo={props.receiveNoteGeneralInfo}
          receiveNoteDeliveryNote={props.receiveNoteDeliveryNote}
          sourceBranch={sourceBranchByDeliveryNote}
          branch={branchByDeliveryNote}
          authorizationProfile={authorizationProfileByReceiveNote}
          warehouse={warehouseByReceiveNote}
          paymentType={paymentTypeByReceiveNote}
          data-testid={suffixTestId('forms.branchBasedInformationReadOnly', props)}
        />
      ))
      .with({isReadOnly: true, isBranchBased: false}, () => (
        <BasicInformationReadOnly
          readOnlyFields={{
            supplier: getCustomerName(supplierByReceiveNote),
            warehouse: props.warehouse?.name,
            authorizationProfile: authorizationProfileByReceiveNote?.name,
            number: props.receiveNoteState?.receiveNoteNumber,
            date:
              isReceiveNoteCompleted && props.receiveNoteState?.completedAt
                ? props.receiveNoteState.completedAt
                : null,
            invoiceNumber: props.receiveNoteGeneralInfo?.invoiceNumber,
            invoiceIssueDate: props.receiveNoteGeneralInfo?.invoiceIssueDate,
            paymentType: paymentTypeByReceiveNote?.value,
            currency: currencyByWarehouse?.code,
            exchangeRate: props.receiveNoteGeneralInfo?.exchangeRate,
            ncConversion: props.receiveNoteGeneralInfo?.ncConversion,
            deliveryNoteNumber: props.receiveNoteDeliveryNote?.deliveryNote?.deliveryNoteNumber,
            issuedAt: props.receiveNoteDeliveryNote?.deliveryNote?.deliveryNoteIssuedAt,
            note: props.receiveNoteGeneralInfo?.note,
          }}
        />
      ))
      .with({isReadOnly: false, isBranchBased: true}, () => (
        <BranchBasedInformationForm
          isCompleted={isReceiveNoteCompleted}
          receiveNoteState={props.receiveNoteState}
          receiveNoteGeneralInfo={props.receiveNoteGeneralInfo}
          receiveNoteDeliveryNote={props.receiveNoteDeliveryNote}
          branchList={branchList}
          warehouses={warehouses}
          authorizationProfiles={authorizationProfiles}
          paymentTypes={paymentTypes}
          onClose={() => setIsReadOnly(true)}
          data-testid={suffixTestId('forms.branchBasedInformation', props)}
        />
      ))
      .with({isReadOnly: false, isBranchBased: false}, () => (
        <BasicInformationForm
          isCompleted={isReceiveNoteCompleted}
          isDraft={props.isDraftReceiveNote}
          receiveNoteState={props.receiveNoteState}
          receiveNoteGeneralInfo={props.receiveNoteGeneralInfo}
          receiveNoteDeliveryNote={props.receiveNoteDeliveryNote}
          suppliers={suppliers}
          warehouses={warehouses}
          authorizationProfiles={authorizationProfiles}
          paymentTypes={paymentTypes}
          currencies={currencies}
          currencyCodeByWarehouse={currencyByWarehouse?.code}
          onClose={() => setIsReadOnly(true)}
          data-testid={suffixTestId('forms.basicInformation', props)}
        />
      ))
      .exhaustive();

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
      <Box>
        <VStack spacing={4}>
          <Card
            title={i18n.t('general.labels.basicInformation')}
            actions={basicInformationCardActions}
            data-testid={suffixTestId('cards.basicInformation', props)}
          >
            {renderBasicInformation(isReadOnly, isBranchBased)}
          </Card>
          <Card
            title={i18n.t('entity.warehouse.labels.deliveryNote')}
            data-testid={suffixTestId('cards.deliveryNote', props)}
          >
            <DataStatus isEmpty={props.isDraftReceiveNote} minHeight={60}>
              <Card variant="inlineGrey">
                <Attributes
                  rows={deliveryNoteAttributes}
                  size="quarter"
                  data-testid={suffixTestId('deliveryNoteAttributes', props)}
                />
              </Card>
            </DataStatus>
          </Card>
        </VStack>
      </Box>
    </DataStatus>
  );
};
